import React, { useState, useRef, useEffect } from "react"
import { getRequest, pathname } from "@react/helpers/fetch"
import Input from "@react/shared/ui/Input"

const AUTOCOMPLETE_PATH = "/products/autocomplete"

// Modern search-as-you-type product picker. Searches the account catalogue via
// Products::Autocomplete, then fetches the full pricing payload via
// Quotes::Builder::BuildProduct (client-aware sale price + VAT). The chosen
// product's data is handed back through onSelect.
const ProductPicker = ({
  clientId,
  onSelect,
  placeholder,
  size     = "md",
  lineType = "Qline",
  autoFocus = false,
}) => {
  const [query,   setQuery]   = useState("")
  const [results, setResults] = useState([])
  const [open,    setOpen]    = useState(false)
  const [loading, setLoading] = useState(false)

  const wrapperRef   = useRef(null)
  const debounceRef  = useRef(null)
  const requestIdRef = useRef(0)

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const runSearch = value => {
    setQuery(value)
    clearTimeout(debounceRef.current)
    if (!value || value.trim().length < 2) {
      setResults([])
      setOpen(false)
      return
    }
    debounceRef.current = setTimeout(() => {
      const requestId = ++requestIdRef.current
      const params    = { q: value.trim() }
      if (clientId) params.client = clientId
      getRequest(pathname({ path: AUTOCOMPLETE_PATH, format: "json", params })).then(data => {
        if (requestId !== requestIdRef.current) return
        setResults(Array.isArray(data) ? data : [])
        setOpen(true)
      })
    }, 250)
  }

  const choose = product => {
    setOpen(false)
    setQuery("")
    setResults([])
    setLoading(true)
    const params = { line_type: lineType }
    if (clientId) params.client = clientId
    getRequest(pathname({ path: `/quotes/form/${product.id}/product`, format: "json", params }))
      .then(data => onSelect(data))
      .finally(() => setLoading(false))
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        variant     = "search"
        size        = {size}
        leadingIcon = "search"
        value       = {query}
        autoFocus   = {autoFocus}
        disabled    = {loading}
        placeholder = {placeholder || I18n.t("quotes.new.product")}
        onChange    = {e => runSearch(e.target.value)}
        onFocus     = {() => { if (results.length) setOpen(true) }}
      />
      {open && results.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-64 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg py-1 text-sm">
          {results.map(product => (
            <li key={product.id}>
              <button
                type        = "button"
                onMouseDown = {event => { event.preventDefault(); choose(product) }}
                className   = "w-full text-left px-3 py-2 hover:bg-brand-50 text-slate-700"
              >
                {product.name}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && !loading && results.length === 0 && query.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg px-3 py-2 text-sm text-slate-500">
          {I18n.t("quotes.qline_service_blueprints.form.product_no_results")}
        </div>
      )}
    </div>
  )
}

export default ProductPicker
